import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LocationAssignmentPicker,
  type WarehouseOption,
} from '@/components/locations';
import { apiFetch } from '@/lib/api';
import { notify } from '@/lib/notify';
import { cn } from '@/lib/utils';

export interface UserFormWizardUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  roles: { id: string; name: string }[];
}

export interface UserFormWizardRole {
  id: string;
  name: string;
}

interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  roleId: string;
}

const INITIAL_FORM: UserFormData = {
  email: '',
  firstName: '',
  lastName: '',
  roleId: '',
};

const SCOPED_ROLES = new Set(['hubManager', 'spokeManager', 'gigWorker']);

interface LocationAssignment {
  warehouseId: string;
}

type WizardStep = 1 | 2;

interface UserFormWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  roles: UserFormWizardRole[];
  user?: UserFormWizardUser | null;
  onSuccess: () => void;
}

async function loadAssignableWarehouses(): Promise<WarehouseOption[]> {
  const res = await apiFetch('/api/v1/warehouses?pageSize=200&isActive=true');
  if (!res.ok) {
    throw new Error('Failed to load locations');
  }
  const data = await res.json();
  const items = (data.items ?? []) as WarehouseOption[];
  return items.filter((warehouse) => warehouse.type === 'HUB' || warehouse.type === 'SPOKE');
}

export function UserFormWizard({
  open,
  onOpenChange,
  mode,
  roles,
  user = null,
  onSuccess,
}: UserFormWizardProps) {
  const [step, setStep] = useState<WizardStep>(1);
  const [formData, setFormData] = useState<UserFormData>(INITIAL_FORM);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<string[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdUserId, setCreatedUserId] = useState<string | null>(null);

  const selectedRoleName = roles.find((role) => role.id === formData.roleId)?.name ?? '';
  const isScopedRole = SCOPED_ROLES.has(selectedRoleName);

  useEffect(() => {
    if (!open) return;

    setStep(1);
    setError(null);
    setSaving(false);
    setCreatedUserId(null);

    if (mode === 'edit' && user) {
      setFormData({
        email: user.email,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        roleId: user.roles[0]?.id || '',
      });
    } else {
      setFormData(INITIAL_FORM);
      setSelectedWarehouseIds([]);
    }

    let cancelled = false;

    async function loadLocations() {
      setLocationsLoading(true);
      try {
        if (mode === 'edit' && user) {
          const [warehouseList, assignRes] = await Promise.all([
            loadAssignableWarehouses(),
            apiFetch(`/api/admin/users/${user.id}/locations`),
          ]);
          if (cancelled) return;
          setWarehouses(warehouseList);
          if (assignRes.ok) {
            const assignData = await assignRes.json();
            const items = (assignData.items ?? []) as LocationAssignment[];
            setSelectedWarehouseIds(items.map((item) => item.warehouseId));
          } else {
            setSelectedWarehouseIds([]);
          }
        } else {
          const warehouseList = await loadAssignableWarehouses();
          if (cancelled) return;
          setWarehouses(warehouseList);
          setSelectedWarehouseIds([]);
        }
      } catch {
        if (!cancelled) {
          setWarehouses([]);
          setSelectedWarehouseIds([]);
          setError('Failed to load locations');
        }
      } finally {
        if (!cancelled) {
          setLocationsLoading(false);
        }
      }
    }

    void loadLocations();

    return () => {
      cancelled = true;
    };
  }, [open, mode, user]);

  function validateDetails(): boolean {
    if (!formData.email || !formData.firstName || !formData.lastName || !formData.roleId) {
      setError('Email, name, and role are required');
      return false;
    }
    setError(null);
    return true;
  }

  function goNext() {
    if (!validateDetails()) return;
    setStep(2);
  }

  async function patchUser(userId: string, baseline?: UserFormWizardUser | null): Promise<void> {
    const updateData: Partial<UserFormData> = {};
    if (!baseline || (formData.email && formData.email !== baseline.email)) {
      updateData.email = formData.email;
    }
    if (!baseline || formData.firstName) updateData.firstName = formData.firstName;
    if (!baseline || formData.lastName) updateData.lastName = formData.lastName;
    if (formData.roleId) updateData.roleId = formData.roleId;

    if (Object.keys(updateData).length === 0) return;

    const res = await apiFetch(`/api/admin/users/${userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updateData),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(
        typeof data.error === 'string' ? data.error : 'Failed to update user'
      );
    }
  }

  async function ensureUserSaved(): Promise<{ userId: string; wasJustCreated: boolean }> {
    if (mode === 'edit') {
      if (!user) {
        throw new Error('No user selected for edit');
      }
      await patchUser(user.id, user);
      return { userId: user.id, wasJustCreated: false };
    }

    if (createdUserId) {
      await patchUser(createdUserId);
      return { userId: createdUserId, wasJustCreated: false };
    }

    const payload: Record<string, string> = {
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      roleId: formData.roleId,
    };

    const res = await apiFetch('/api/admin/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(
        typeof data.error === 'string' ? data.error : 'Failed to create user'
      );
    }
    const created = (await res.json()) as { id?: string };
    if (typeof created.id !== 'string' || !created.id) {
      throw new Error('User created but response was missing an id');
    }
    setCreatedUserId(created.id);
    return { userId: created.id, wasJustCreated: true };
  }

  async function saveLocations(userId: string): Promise<void> {
    const res = await apiFetch(`/api/admin/users/${userId}/locations`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ warehouseIds: selectedWarehouseIds }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(
        typeof data.error === 'string' ? data.error : 'Failed to save locations'
      );
    }
  }

  async function finish() {
    setSaving(true);
    setError(null);
    try {
      const { userId, wasJustCreated } = await ensureUserSaved();
      try {
        await saveLocations(userId);
      } catch (locationErr) {
        const locationMessage =
          locationErr instanceof Error ? locationErr.message : 'Failed to save locations';
        throw new Error(
          mode === 'create' || wasJustCreated
            ? `User created, but locations failed: ${locationMessage}. You can retry.`
            : locationMessage
        );
      }

      if (mode === 'create') {
        notify.success('User created', 'A set-password invite email was sent.');
      } else {
        notify.success('User updated');
      }
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save user');
    } finally {
      setSaving(false);
    }
  }

  const title = mode === 'create' ? 'Add user' : 'Edit user';
  const finishLabel = mode === 'create' ? 'Create user' : 'Save changes';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-4xl flex-col gap-4 overflow-hidden">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {step === 1
              ? mode === 'create'
                ? 'Enter profile details and choose a role. They will set their password from the invite email.'
                : 'Update profile details and role.'
              : 'Select hubs and spokes this user can access.'}
          </DialogDescription>
        </DialogHeader>

        <nav aria-label="Wizard steps" className="flex items-center gap-3 text-sm">
          <span
            className={cn(
              step === 1 ? 'font-medium text-foreground' : 'text-muted-foreground'
            )}
          >
            1. Details
          </span>
          <span className="text-muted-foreground" aria-hidden>
            —
          </span>
          <span
            className={cn(
              step === 2 ? 'font-medium text-foreground' : 'text-muted-foreground'
            )}
          >
            2. Locations
          </span>
        </nav>

        {step === 1 ? (
          <div className="grid gap-4 overflow-y-auto">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="wizard-firstName">First Name</Label>
                <Input
                  id="wizard-firstName"
                  value={formData.firstName}
                  onChange={(event) =>
                    setFormData((prev) => ({ ...prev, firstName: event.target.value }))
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="wizard-lastName">Last Name</Label>
                <Input
                  id="wizard-lastName"
                  value={formData.lastName}
                  onChange={(event) =>
                    setFormData((prev) => ({ ...prev, lastName: event.target.value }))
                  }
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="wizard-email">Email</Label>
              <Input
                id="wizard-email"
                type="email"
                value={formData.email}
                onChange={(event) =>
                  setFormData((prev) => ({ ...prev, email: event.target.value }))
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="wizard-role">Role</Label>
              <Select
                value={formData.roleId}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, roleId: value }))
                }
              >
                <SelectTrigger id="wizard-role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <LocationAssignmentPicker
            warehouses={warehouses}
            selectedWarehouseIds={selectedWarehouseIds}
            onSelectedWarehouseIdsChange={setSelectedWarehouseIds}
            loading={locationsLoading}
            showScopedWarning={isScopedRole}
          />
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={goNext}>Next</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(1)} disabled={saving}>
                Back
              </Button>
              <Button onClick={() => void finish()} disabled={saving || locationsLoading}>
                {saving ? 'Saving…' : finishLabel}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
